import { Injectable } from '@angular/core'
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects'
import {
    debounceTime,
    map,
    EMPTY,
    mergeMap,
    catchError,
    switchMap,
    take,
    takeUntil,
    tap,
    timer,
    of,
} from 'rxjs'
import { ViewerService } from '../../case-viewer/neuroglancer-frame/viewer.service'
import { viewerActions } from './viewer.actions'
import {
    selectSelectableAnnotationLayerUrls,
    selectSelectableSegmentationLayerNames,
    selectViewerState,
} from './viewer.selectors'
import { getColorForSelection } from '../../shared/utils/color-utils'
import { Store } from '@ngrx/store'
import {
    selectSelectionPoints,
    selectSavedAndCurrentFreeSelections,
    selectSavedAndCurrentHierarchicalSelections,
} from '../selection/selection.selectors'
import { selectionActions } from '../selection/selection.actions'
import { SelectionsForCaseList } from '../selection/selection.state'
import { mapViewerStateToNeuroglancerConfig } from './viewer-state-utils'
import { defaultDebounceTime } from '../../shared/utils/rxjs-pipelines'
import { DatasetApiService } from '../../shared/services/dataset-api.service'
import { RouterUtilsService } from '../../shared/services/router-utils.service'
import { userActions } from '../user/user.actions'
import { selectCaseId, selectCaseSegObjectPropertyMap } from '../case/case.selectors'

@Injectable()
export class ViewerEffects {
    constructor(
        private readonly actions$: Actions,
        private readonly viewerService: ViewerService,
        private readonly datasetApi: DatasetApiService,
        private readonly store: Store,
        private readonly routerUtils: RouterUtilsService
    ) {}

    setQueryParamPosition$ = createEffect(
        () => {
            return this.viewerService.position$.pipe(
                defaultDebounceTime(),
                tap(position => {
                    if (position) {
                        this.routerUtils.updatePositionQueryParam([
                            position[0],
                            position[1],
                            position[2],
                        ])
                    }
                })
            )
        },
        { dispatch: false }
    )

    setQueryParamZoom$ = createEffect(
        () => {
            return this.viewerService.zoom$.pipe(
                defaultDebounceTime(),
                tap(zoom => {
                    if (zoom) this.routerUtils.updateZoomQueryParam(zoom)
                })
            )
        },
        { dispatch: false }
    )

    // setQueryParamLayerConfig$ = createEffect(
    //     () => {
    //         return this.actions$.pipe(
    //             ofType(
    //                 viewerActions.setLayerOpacity,
    //                 viewerActions.setLayerVisibility,
    //                 viewerActions.setLayerShaderColor,
    //                 viewerActions.setLayerOpacityUnselected,
    //                 viewerActions.setLayerSaturation,
    //                 viewerActions.setLayerBrightness,
    //                 viewerActions.setLayerContrast,
    //                 viewerActions.setLayerSegmentationColor
    //             ),
    //             defaultDebounceTime(),
    //             concatLatestFrom(() => this.store.select(selectLayers)),
    //             tap(([, layers]) => {
    //                 if (layers && layers.length > 0) {
    //                     this.routerUtils.updateLayerQueryParam(layers)
    //                 }
    //             })
    //         )
    //     },
    //     { dispatch: false }
    // )

    updateSegmentationViewerSelection$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(
                    selectionActions.updateSelection,
                    selectionActions.clearSelection,
                    selectionActions.setFreeSelection,
                    selectionActions.setHierarchicalSelectionRoot,
                    selectionActions.setHierarchicalSelectionNode
                ),
                concatLatestFrom(() => [
                    this.store.select(selectSelectionPoints),
                    this.store.select(selectSelectableSegmentationLayerNames),
                    this.store.select(selectCaseSegObjectPropertyMap),
                ]),
                mergeMap(([_, selection, layerNames, SegObjectPropertyMap]) => {
                    // Use SegObjectPropertyMap.segSource directly
                    return of({ segSource: SegObjectPropertyMap?.segSource, layerNames, selection })
                }),
                tap(({ segSource, layerNames, selection }) => {
                    let matchedLayerFound = false
                    const layers = layerNames.map((name: string) =>
                        this.viewerService.getSegmentationLayerByName(name)
                    )

                    // Check if any segmentation layer has a matching URL
                    layers.forEach(layer => {
                        if (!layer) return
                        if (segSource === layer.dataSources![0]!.spec.url) {
                            matchedLayerFound = true
                        }
                    })

                    // Update the segmentation viewer state for each layer
                    layers.forEach(layer => {
                        if (!layer) return

                        if (matchedLayerFound && segSource !== layer.dataSources![0]!.spec.url)
                            return

                        const segmentationGroupState =
                            layer.displayState.segmentationGroupState.value
                        segmentationGroupState.visibleSegments.clear()
                        segmentationGroupState.restoreState({
                            segments:
                                selection.size < 1e6
                                    ? Array.from(selection).map(c => c.toString())
                                    : [],
                        })
                    })
                }),
                catchError(err => {
                    console.error('Error fetching marker mapping:', err)
                    return EMPTY
                })
            )
        },
        { dispatch: false }
    )

    reRenderAnnotationLayer$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(
                    selectionActions.loadingFreeSelectionsSucceeded,
                    selectionActions.loadingHierarchicalSelectionsSucceeded,
                    selectionActions.loadingUnsupervisedSelectionListSucceeded,
                    selectionActions.setFreeSelection,
                    selectionActions.updateSelection,
                    selectionActions.clearSelection,
                    // TODO: updating only after saving makes the app less snappy - use triggering action and revert on fail?
                    selectionActions.savingFreeSelectionSucceeded,
                    selectionActions.deletingFreeSelectionSucceeded,
                    selectionActions.deletingHierarchicalSelectionRootSucceeded,
                    selectionActions.savingHierarchicalSelectionRootSucceeded,
                    selectionActions.setSelectionVisibility,
                    selectionActions.setSelectionOpacity,
                    selectionActions.setSelectionShaderColor
                ),
                concatLatestFrom(() => this.store.select(selectSelectableAnnotationLayerUrls)),
                concatLatestFrom(() => this.store.select(selectSavedAndCurrentFreeSelections)),
                concatLatestFrom(() =>
                    this.store.select(selectSavedAndCurrentHierarchicalSelections)
                ),
                tap(([[[, layerSources], freeSelections], hierarchicalSelections]) => {
                    this.updateAnnotationLayerSelections(
                        [...freeSelections, ...hierarchicalSelections],
                        layerSources
                    )
                })
            )
        },
        { dispatch: false }
    )

    updateViewerConfig$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(viewerActions.configLoaded),
                concatLatestFrom(() => this.store.select(selectViewerState)),
                tap(([, state]) => {
                    this.viewerService.setViewerConfig(mapViewerStateToNeuroglancerConfig(state))
                })
            )
        },
        { dispatch: false }
    )

    postViewerStateOnStateChanged$ = createEffect(
        () => {
            return this.store
                .select(selectCaseId)
                .pipe(
                    switchMap(() =>
                        this.actions$.pipe(
                            ofType(
                                viewerActions.setZoom,
                                viewerActions.setPosition,
                                viewerActions.setExpandedNodes,
                                viewerActions.setLayout,
                                viewerActions.setLayerOpacity,
                                viewerActions.setLayerVisibility,
                                viewerActions.setLayerShaderColor,
                                viewerActions.setLayerSaturation,
                                viewerActions.setLayerOpacityUnselected,
                                viewerActions.setLayerBrightness,
                                viewerActions.setLayerContrast,
                                viewerActions.setLayerSegmentationColor
                            ),
                            debounceTime(2000),
                            this.datasetApi.saveViewerState()
                        )
                    )
                )
        },
        { dispatch: false }
    )

    postViewerStateOnLogout$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(userActions.startLogout),
            this.datasetApi.saveViewerState(),
            map(() => userActions.logoutViewerStateSent())
        )
    })

    disposeViewer$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(userActions.logoutFinished),
                tap(() => {
                    this.viewerService.disposeViewer()
                })
            )
        },
        { dispatch: false }
    )

    private updateAnnotationLayerSelections(
        selections: SelectionsForCaseList,
        layerSources: string[]
    ): void {
        layerSources.forEach(source =>
            this.viewerService
                .getAnnotationLayerNameBySource$(source)
                .pipe(take(1), takeUntil(timer(5000)))
                .subscribe(layerName => {
                    if (layerName) {
                        this.viewerService
                            .getAnnotationLayerByName$(layerName)
                            .pipe(take(1))
                            .subscribe(async renderLayer => {
                                // TODO: move jobSettings to a state, request is slow
                                const jobSettingsUrl =
                                    renderLayer.userLayer?.dataSources[0]?.spec?.url.replace(
                                        'precomputed://',
                                        ''
                                    ) + '/job_settings.json'
                                const jobSettings = await fetch(jobSettingsUrl).then(res =>
                                    res.json()
                                )

                                const id2color = new Map<number, number>()

                                for (const selection of selections) {
                                    if (
                                        selection.annotationVisible &&
                                        selection.annotationOpacity > 0 &&
                                        selection.segSource === jobSettings.seg_url
                                    ) {
                                        const color = getColorForSelection(selection)
                                        for (const id of selection.points) {
                                            id2color.set(id, color)
                                        }
                                    }
                                }

                                // PoC hack (see: src/neuroglancer/annotation/renderlayer.ts)
                                renderLayer.getPropertiesForId = (id: number) => {
                                    // Performance-critical: changing active selection or toggling opacity
                                    return id2color.get(id) ?? 0
                                }
                                // Trigger redraw after updating properties
                                renderLayer.redrawNeeded.dispatch()
                            })
                    }
                })
        )
    }
}
